// QuizMap — selection-aware wrapper around react-simple-maps for the
// Atlas Map Quiz POC.
//
// Modes are driven by props, not internal state:
//   - `selectableEntityIds` (non-null) → Type A map_identify behaviour:
//     cursor: pointer, hover highlight, click selects an entity.
//   - `highlightedEntityIds` (non-null, `selectableEntityIds` null) → Type B/C:
//     context-only — those entities are visually emphasised but the map is
//     not clickable.
//
// Two geo sources:
//   - `thirteen_colonies`: custom low-res GeoJSON in data/atlas_quizzes/geo/.
//     One feature per colony, id = state abbreviation.
//   - `world_period_2`: existing public/data/atlas/basemap-50m.json; empires
//     are painted by joining ISO_A3 codes (from period_2_world_country_map.json)
//     against country names via iso_a3_to_name.json.

import { useMemo, useRef, useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { useTheme } from '../../../lib/ThemeContext';

import thirteenColoniesGeo from '../../../data/atlas_quizzes/geo/thirteen_colonies.json';
import empireMapData from '../../../data/atlas_quizzes/geo/period_2_world_country_map.json';
import isoA3ToName from '../../../data/atlas_quizzes/geo/iso_a3_to_name.json';

const WORLD_BASEMAP_URL = '/data/atlas/basemap-50m.json';

// For the 13-colonies quiz we only want NA/Caribbean context behind the
// colonies. Rendering every country at scale=1600 caused degenerate Mercator
// paths from far-hemisphere countries to cover the viewport.
const NORTH_AMERICAN_COUNTRIES = new Set([
  'Canada',
  'United States of America',
  'Mexico',
  'Cuba',
  'Jamaica',
  'Haiti',
  'Dominican Rep.',
  'Bahamas',
  'Greenland',
  'Puerto Rico',
  'Belize',
  'Guatemala',
  'Honduras',
  'El Salvador',
  'Nicaragua',
  'Costa Rica',
  'Panama',
]);

// Muted historical-atlas palette for empire fills (Period 2 only).
// Deliberately desaturated — avoid bright primary colors; think old-atlas tones.
// Per design philosophy: this is one of the few places chromatic color is
// justified, because the story *is* "empires are spatial blocks."
const EMPIRE_COLORS = {
  ottoman:    '#b08a5e', // muted ochre
  safavid:    '#7e9e7a', // sage green
  mughal:     '#b07265', // terracotta
  ming_qing:  '#6d7c9a', // slate blue
  tokugawa:   '#b88a8a', // dusty rose
  russian:    '#8a7a94', // dusty plum
  spanish:    '#c8a964', // warm gold
  portuguese: '#6d9a9a', // muted teal
  songhai:    '#9e7e5c', // clay brown
};

// Reveal / selection colors per design brief §7.
const COLOR = {
  selectedOutline: '#b45309', // amber-700
  selectedFill:    'rgba(180, 83, 9, 0.15)',
  correctOutline:  '#16a34a', // green-600
  correctFill:     'rgba(22, 163, 74, 0.15)',
  wrongOutline:    '#dc2626', // red-600
  wrongFill:       'rgba(220, 38, 38, 0.12)',
};

function lightPalette() {
  return {
    oceanBg:        '#eef3f7', // barely-blue, almost off-white
    basemapFill:    '#e5e7eb', // gray-200 — quiet neutral land
    basemapStroke:  '#9ca3af', // gray-400 — readable but not loud
    baseFill:       '#f5f5f5',
    baseStroke:     '#6b7280', // gray-500 for the colonies' default outline
    highlightFill:  '#d4d4d4',
    highlightStroke:'#4b5563', // gray-600
    hoverFill:      '#cbd5e1', // slate-300 — a touch darker than basemap
    hoverStroke:    '#1f2937', // gray-800 — clear outline on hover
  };
}

function darkPalette() {
  return {
    oceanBg:        '#0f172a', // slate-900
    basemapFill:    '#374151', // gray-700
    basemapStroke:  '#6b7280', // gray-500
    baseFill:       '#1f2937', // gray-800
    baseStroke:     '#9ca3af', // gray-400
    highlightFill:  '#4b5563',
    highlightStroke:'#d1d5db',
    hoverFill:      '#475569', // slate-600
    hoverStroke:    '#f3f4f6', // gray-100
  };
}

// Invert the empire map so we can look up an empire from a country name.
function buildCountryNameToEmpire() {
  const out = {};
  const empires = empireMapData.empires || {};
  for (const [empireId, isoList] of Object.entries(empires)) {
    for (const iso of isoList) {
      const name = isoA3ToName[iso];
      if (name) out[name] = empireId;
    }
  }
  return out;
}

export default function QuizMap({
  geoSource,                 // 'thirteen_colonies' | 'world_period_2'
  highlightedEntityIds,      // string[] | null
  selectableEntityIds,       // string[] | null
  selectedEntityId,          // string | null
  correctEntityId,           // string | null (after submit)
  onEntitySelect,            // (id) => void
}) {
  const { dark } = useTheme();
  const palette = dark ? darkPalette() : lightPalette();
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 960, height: 600 });

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width && height) setDimensions({ width, height });
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const isSelectableMode = Array.isArray(selectableEntityIds);
  const highlightSet = useMemo(
    () => new Set(highlightedEntityIds || []),
    [highlightedEntityIds]
  );
  const selectableSet = useMemo(
    () => new Set(selectableEntityIds || []),
    [selectableEntityIds]
  );

  const countryToEmpire = useMemo(buildCountryNameToEmpire, []);

  if (geoSource === 'thirteen_colonies') {
    const basemapStyle = {
      default: { fill: palette.basemapFill, stroke: palette.basemapStroke, strokeWidth: 0.75, outline: 'none' },
      hover:   { fill: palette.basemapFill, stroke: palette.basemapStroke, strokeWidth: 0.75, outline: 'none' },
      pressed: { fill: palette.basemapFill, stroke: palette.basemapStroke, strokeWidth: 0.75, outline: 'none' },
    };
    return (
      <div
        ref={containerRef}
        className="w-full h-full relative rounded-lg overflow-hidden"
        style={{ background: palette.oceanBg }}
      >
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{ center: [-76, 39], scale: 1600 }}
          width={dimensions.width}
          height={dimensions.height}
          style={{ width: '100%', height: '100%' }}
        >
          <Geographies geography={WORLD_BASEMAP_URL}>
            {({ geographies }) =>
              geographies
                .filter((geo) => NORTH_AMERICAN_COUNTRIES.has(geo.properties?.name))
                .map((geo) => (
                  <Geography
                    key={`basemap-${geo.rsmKey}`}
                    geography={geo}
                    style={basemapStyle}
                    tabIndex={-1}
                  />
                ))
            }
          </Geographies>
          <Geographies geography={thirteenColoniesGeo}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const id = geo.properties?.id;
                return (
                  <ColonyGeography
                    key={geo.rsmKey}
                    geo={geo}
                    entityId={id}
                    palette={palette}
                    isSelectableMode={isSelectableMode}
                    selectableSet={selectableSet}
                    highlightSet={highlightSet}
                    selectedEntityId={selectedEntityId}
                    correctEntityId={correctEntityId}
                    onEntitySelect={onEntitySelect}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>
    );
  }

  // world_period_2: render every country; fill by empire membership.
  return (
    <div
      ref={containerRef}
      className="w-full h-full relative rounded-lg overflow-hidden"
      style={{ background: palette.oceanBg }}
    >
      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{ scale: 170, center: [10, 20] }}
        width={dimensions.width}
        height={dimensions.height}
        style={{ width: '100%', height: '100%' }}
      >
        <Geographies geography={WORLD_BASEMAP_URL}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const name = geo.properties?.name;
              const empire = name ? countryToEmpire[name] : null;
              return (
                <EmpireGeography
                  key={geo.rsmKey}
                  geo={geo}
                  empireId={empire}
                  palette={palette}
                  isSelectableMode={isSelectableMode}
                  selectableSet={selectableSet}
                  highlightSet={highlightSet}
                  selectedEntityId={selectedEntityId}
                  correctEntityId={correctEntityId}
                  onEntitySelect={onEntitySelect}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Per-feature renderers

function ColonyGeography({
  geo,
  entityId,
  palette,
  isSelectableMode,
  selectableSet,
  highlightSet,
  selectedEntityId,
  correctEntityId,
  onEntitySelect,
}) {
  const isSelectable = isSelectableMode && selectableSet.has(entityId);
  const isHighlighted = !isSelectableMode && highlightSet.has(entityId);
  const isSelected = selectedEntityId === entityId;
  const revealed = correctEntityId != null;
  const isCorrect = revealed && correctEntityId === entityId;
  const pickedWrong =
    revealed && isSelected && correctEntityId !== entityId;

  const style = buildEntityStyle({
    palette,
    isSelectable,
    isHighlighted,
    isSelected,
    isCorrect,
    pickedWrong,
    revealed,
  });

  const handleClick = () => {
    if (!isSelectable || revealed) return;
    onEntitySelect?.(entityId);
  };

  const handleKeyDown = (evt) => {
    if (!isSelectable || revealed) return;
    if (evt.key !== 'Enter' && evt.key !== ' ') return;
    evt.preventDefault();
    onEntitySelect?.(entityId);
  };

  return (
    <Geography
      geography={geo}
      style={style}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={isSelectable && !revealed ? 0 : -1}
      data-testid={`quiz-region-${entityId}`}
    />
  );
}

function EmpireGeography({
  geo,
  empireId,
  palette,
  isSelectableMode,
  selectableSet,
  highlightSet,
  selectedEntityId,
  correctEntityId,
  onEntitySelect,
}) {
  // Countries not belonging to any quiz-defined empire stay as base map.
  const inSelectable = empireId && isSelectableMode && selectableSet.has(empireId);
  const inHighlight  = empireId && !isSelectableMode && highlightSet.has(empireId);
  const isSelected   = empireId && selectedEntityId === empireId;
  const revealed     = correctEntityId != null;
  const isCorrect    = empireId && revealed && correctEntityId === empireId;
  const pickedWrong  =
    empireId && revealed && isSelected && correctEntityId !== empireId;

  const empireColor = empireId ? EMPIRE_COLORS[empireId] : null;
  const style = buildEmpireStyle({
    palette,
    empireColor,
    inSelectable,
    inHighlight,
    isSelected,
    isCorrect,
    pickedWrong,
    revealed,
  });

  const handleClick = () => {
    if (!inSelectable || revealed) return;
    onEntitySelect?.(empireId);
  };

  const handleKeyDown = (evt) => {
    if (!inSelectable || revealed) return;
    if (evt.key !== 'Enter' && evt.key !== ' ') return;
    evt.preventDefault();
    onEntitySelect?.(empireId);
  };

  return (
    <Geography
      geography={geo}
      style={style}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={inSelectable && !revealed ? 0 : -1}
      data-testid={empireId ? `quiz-region-${empireId}` : undefined}
    />
  );
}

// -----------------------------------------------------------------------------
// Style builders

function buildEntityStyle({
  palette,
  isSelectable,
  isHighlighted, // eslint-disable-line no-unused-vars
  isSelected,
  isCorrect,
  pickedWrong,
  revealed,
}) {
  // Plain outlines by default — transparent (not 'none') so the interior
  // catches pointer events for hover/click, not just the stroke.
  let fill = 'transparent';
  let stroke = palette.baseStroke;
  let strokeWidth = 0.8;
  const cursor = isSelectable && !revealed ? 'pointer' : 'default';

  if (isSelected && !revealed) {
    fill = COLOR.selectedFill;
    stroke = COLOR.selectedOutline;
    strokeWidth = 2;
  }

  if (isCorrect) {
    fill = COLOR.correctFill;
    stroke = COLOR.correctOutline;
    strokeWidth = 2.5;
  } else if (pickedWrong) {
    fill = COLOR.wrongFill;
    stroke = COLOR.wrongOutline;
    strokeWidth = 2.5;
  }

  const baseStyle = {
    fill,
    stroke,
    strokeWidth,
    outline: 'none',
    cursor,
    transition: 'all 150ms ease-out',
  };

  // Hover style only applies when the shape isn't already showing a
  // reveal/selected state — we don't want hover to repaint over the
  // correct/wrong/selected colors.
  const inRevealState = isSelected || isCorrect || pickedWrong;
  const hoverStyle = inRevealState
    ? baseStyle
    : {
        ...baseStyle,
        fill: palette.hoverFill,
        stroke: palette.hoverStroke,
        strokeWidth: 1.2,
      };

  return {
    default: baseStyle,
    hover:   hoverStyle,
    pressed: baseStyle,
  };
}

function buildEmpireStyle({
  palette,
  empireColor,
  inSelectable,
  inHighlight,
  isSelected,
  isCorrect,
  pickedWrong,
  revealed,
}) {
  let fill = palette.baseFill;
  let stroke = palette.baseStroke;
  let strokeWidth = 0.4;
  let cursor = 'default';

  if (empireColor && (inHighlight || inSelectable)) {
    fill = empireColor;
    stroke = palette.highlightStroke;
    strokeWidth = 0.6;
  }

  if (inSelectable) {
    cursor = revealed ? 'default' : 'pointer';
  }

  if (isSelected && !revealed) {
    stroke = COLOR.selectedOutline;
    strokeWidth = 2;
  }

  if (isCorrect) {
    stroke = COLOR.correctOutline;
    strokeWidth = 2.5;
  } else if (pickedWrong) {
    stroke = COLOR.wrongOutline;
    strokeWidth = 2.5;
  }

  const hoverStyle =
    inSelectable && !revealed && !isSelected
      ? {
          fill,
          stroke: palette.hoverStroke,
          strokeWidth: 1.2,
          outline: 'none',
          cursor,
          transition: 'all 150ms ease-out',
        }
      : { fill, stroke, strokeWidth, outline: 'none', cursor };

  const baseStyle = {
    fill,
    stroke,
    strokeWidth,
    outline: 'none',
    cursor,
    transition: 'all 200ms ease-out',
  };

  return {
    default: baseStyle,
    hover:   hoverStyle,
    pressed: baseStyle,
  };
}

// Exported for use by QuizResults legend, etc.
export { EMPIRE_COLORS };
